import { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { useJwtRefresh } from "./useJwtRefresh";
import {
  fetchWebhookStream,
  createConversation,
  uploadFiles,
} from "../utils/chatApi";
import type { AIResponse, Message } from "../utils/types";
import {
  createUserMessage,
  createAssistantPlaceholder,
  mapHistoricMessages,
} from "./utils";
import { readAssistantStream } from "../utils/streamReader";
import { fetchMockStream } from "../utils/mockStream";
import { RootState } from "src/redux/reducers";
import { clearAIChatContext } from "src/redux/ai-chat/aiChatActions";
import { showErrorToast } from "src/utils/methods";
import { handleError } from "src/utils/error";
import { AppConfig } from "src/config/AppConfig";

// Local-only: replay a saved webhook response from mockResponse.txt instead of
// hitting the real endpoint. Flip MOCK_STREAM_ENABLED to toggle; it is ANDed
// with the local-dev check so it can never activate outside local development.
const MOCK_STREAM_ENABLED = false;
const USE_MOCK_STREAM = AppConfig.isLocalEnv() && MOCK_STREAM_ENABLED;

export function useAIChat() {
  const dispatch = useDispatch();
  const { getValidToken } = useJwtRefresh();
  const identity = useSelector(
    (state: RootState) => state.mainReducer.identity,
  );
  const chatwootId = identity?.chatwootId?.id;
  const isWorkspaceHostnameCustomized = identity?.isWorkspaceHostnameCustomized;
  const wsMemberSelectedContext = useSelector(
    (state: RootState) => state.aiChatReducer.wsMemberSelectedContext,
  );
  const sessionIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [messages, setMessages] = useState<AIResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isConversationArchived, setIsConversationArchived] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<
    number | null
  >(null);
  const conversationCreatedRef = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const removeMessage = (id: string) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  // Resolve the session id for this message, creating the conversation on the
  // first message when needed. Returns the session id, or null on failure
  // (after cleaning up the optimistic user message and clearing loading).
  const ensureSession = async (
    chatInput: string,
    userMessageId: string,
  ): Promise<string | null> => {
    // When simulating a stream locally, skip the real conversation creation
    // request and use a dummy session id.
    if (USE_MOCK_STREAM) {
      sessionIdRef.current = sessionIdRef.current ?? "mock-session";
      conversationCreatedRef.current = true;
    }

    // Check if this is the first message and create conversation
    if (!USE_MOCK_STREAM && !conversationCreatedRef.current && chatwootId) {
      try {
        const jwtToken = await getValidToken();
        if (!jwtToken) {
          throw new Error("No valid token");
        }
        const conversationId = await createConversation(
          chatwootId,
          jwtToken,
          chatInput,
        );
        sessionIdRef.current = conversationId.toString();
        conversationCreatedRef.current = true;
      } catch (error) {
        console.error("Failed to create conversation:", error);
        showErrorToast();
        removeMessage(userMessageId);
        setIsLoading(false);
        return null;
      }
    }

    // Ensure we have a session ID before proceeding
    if (!sessionIdRef.current) {
      console.error("No session ID available");
      showErrorToast();
      removeMessage(userMessageId);
      setIsLoading(false);
      return null;
    }

    return sessionIdRef.current;
  };

  const resetAbortController = (): AbortController => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller;
  };

  const openStream = async (
    chatInput: string,
    sessionId: string,
    signal: AbortSignal,
    attachedFiles?: string[],
    predefinedPrompt?: string,
  ): Promise<ReadableStreamDefaultReader<Uint8Array>> => {
    if (USE_MOCK_STREAM) {
      return fetchMockStream();
    }
    const jwtToken = await getValidToken();
    if (!jwtToken) {
      throw new Error("No valid token");
    }
    return fetchWebhookStream(
      chatInput,
      sessionId,
      jwtToken,
      signal,
      attachedFiles,
      wsMemberSelectedContext,
      isWorkspaceHostnameCustomized,
      predefinedPrompt,
    );
  };

  // Uploads staged files (if any) and returns the names to attach to the
  // outgoing message, or null on failure (after surfacing an error toast and
  // cleaning up the optimistic user message + loading state).
  const uploadStagedFiles = async (
    files: File[],
    sessionId: string,
    userMessageId: string,
  ): Promise<string[] | null> => {
    if (!files.length || USE_MOCK_STREAM) return [];

    setIsUploading(true);
    try {
      const jwtToken = await getValidToken();
      if (!jwtToken) {
        throw new Error("No valid token");
      }
      return await uploadFiles(files, sessionId, jwtToken);
    } catch (error) {
      handleError(error);
      removeMessage(userMessageId);
      setIsLoading(false);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleStreamError = (error: unknown, assistantId: string) => {
    if (error instanceof DOMException && error.name === "AbortError") {
      setMessages((prev) => {
        const msg = prev.find((m) => m.id === assistantId);
        if (!msg) return prev;
        if (!msg.content && !msg.thinking) {
          return prev.filter((m) => m.id !== assistantId);
        }
        return prev.map((m) =>
          m.id === assistantId ? { ...m, isStreaming: false } : m,
        );
      });
      return;
    }

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === assistantId
          ? {
              ...msg,
              content: "Sorry, I encountered an error. Please try again.",
              isStreaming: false,
            }
          : msg,
      ),
    );
    setIsLoading(false);
  };

  const sendMessage = async (
    chatInput: string,
    files: File[] = [],
    predefinedPrompt?: string,
  ): Promise<boolean> => {
    if (!chatInput.trim()) return false;

    const attachmentMeta = files.map((file) => ({
      name: file.name,
      mimeType: file.type,
    }));
    const displayMessage = createUserMessage(
      chatInput,
      attachmentMeta,
      wsMemberSelectedContext ?? undefined,
    );
    setMessages((prev) => [...prev, displayMessage]);
    setIsLoading(true);

    const sessionId = await ensureSession(chatInput, displayMessage.id);
    if (!sessionId) return false;

    const attachedFiles = await uploadStagedFiles(
      files,
      sessionId,
      displayMessage.id,
    );
    if (attachedFiles === null) return false;

    const controller = resetAbortController();
    const assistant = createAssistantPlaceholder();
    setMessages((prev) => [...prev, assistant]);

    try {
      const reader = await openStream(
        chatInput,
        sessionId,
        controller.signal,
        attachedFiles,
        predefinedPrompt,
      );
      dispatch(clearAIChatContext());
      await readAssistantStream(
        reader,
        assistant.id,
        setMessages,
        setIsLoading,
      );
    } catch (error) {
      handleStreamError(error, assistant.id);
    }
    return true;
  };

  const abortRequest = () => {
    abortControllerRef.current?.abort();
    setIsLoading(false);
    setIsUploading(false);
    setMessages((prev) =>
      prev.map((msg) =>
        msg.isStreaming ? { ...msg, isStreaming: false } : msg,
      ),
    );
  };

  const startNewChat = () => {
    abortControllerRef.current?.abort();
    sessionIdRef.current = null;
    conversationCreatedRef.current = false;
    setIsLoading(false);
    setIsUploading(false);
    setIsConversationArchived(false);
    setSelectedConversationId(null);
    setMessages([]);
  };

  const loadConversation = (
    conversationId: number,
    apiMessages: Message[],
    isArchived?: boolean,
  ) => {
    abortControllerRef.current?.abort();
    sessionIdRef.current = conversationId.toString();
    conversationCreatedRef.current = true;
    setIsLoading(false);
    setIsConversationArchived(isArchived ?? false);
    setSelectedConversationId(conversationId);
    setMessages(
      mapHistoricMessages(
        apiMessages.filter((msg) => msg.content !== "[empty AI response]"),
      ),
    );
  };

  const getCurrentConversationId = (): number | null => {
    return sessionIdRef.current ? parseInt(sessionIdRef.current, 10) : null;
  };

  return {
    messages,
    isLoading,
    isUploading,
    isConversationArchived,
    sendMessage,
    abortRequest,
    loadConversation,
    startNewChat,
    getCurrentConversationId,
    selectedConversationId,
    setSelectedConversationId,
  };
}
